import { animate, state, style, transition, trigger } from "@angular/animations";
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { GetCountComponent } from "../get-count/get-count.component";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import { CountCalculatorService } from "src/app/services/count-calculator.service";
import { ActionButtonContainer, ActionsButton, SaveButton, SlideInOut } from "@utils/action";
import { DialogService } from "../../services/dialog/dialog.service";
import { StructureService } from "../../services/structure/structure.service";
import {
    ButtonData,
    ModelQuantityJson,
    StructureBaseModel,
    StructureInstance,
    StructureResponseMessage,
    StructureStatus,
} from "@models/structure.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CountOutputData } from "../../../../../utils/count-calculator/count-calculator.model";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { ActivatedRoute, Router } from "@angular/router";
import { filter, finalize, mergeMap, Subscription, switchMap, tap } from "rxjs";
import { InteractionService } from "../../shared/helpers/interaction.service";
import { ActionHelperService } from "../../action-helper.service";
import { Mesh, Object3D } from "three";
import { HelperService } from "../../shared/helpers/helper.service";
import { environment } from "~/src/environments/environment";
import { S3UploadService } from "../../services/s3-upoad/s3-upload.service";
import { SceneManagerService } from "../../services/scene-manager/scene-manager.service";
import { S3UploadCategoryName, S3UploadFileType } from "@models/fileUpload.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { CustomTourService } from "../../services/custom-tour.service";
import { DesignerTourSteps } from "~/src/app/shared/models/designer-tour.model";
import { UndoRedoService } from "../../services/undoRedo/undo-redo.service";
import { Action } from "~/src/app/shared/models/undoRedo.model";
import { InvalidShapeService } from "../../services/invalid-shape.service";
import { TranslateService } from "@ngx-translate/core";
import { CopyPasteService } from "../../services/drag-and-drop/copypaste.service";
import { Object3DUserData } from "../../../../../utils/shape";
import { MeshVisualService } from "../../services/designer/mesh-visual.service";
import { AuthService } from "../../services/auth/auth.service";

@Component({
    selector: "action-button-container",
    templateUrl: "./action-button-container.component.html",
    styleUrls: ["./action-button-container.component.scss"],
    animations: [
        trigger(SlideInOut.triggerName, [
            state(
                SlideInOut.trueState,
                style({
                    top: SlideInOut.topPos,
                    right: SlideInOut.rightPoswithStateTrue,
                }),
            ),
            state(
                SlideInOut.falseState,
                style({
                    top: SlideInOut.topPos,
                    right: SlideInOut.rightPoswithStateFalse,
                }),
            ),
            transition(SlideInOut.transition_true_to_false, animate(SlideInOut.animate_duration)),
            transition(SlideInOut.transition_false_to_true, animate(SlideInOut.animate_duration)),
        ]),
    ],
    standalone: false,
})
export class ActionButtonContainerComponent implements OnInit, OnDestroy {
    public isOpen = false;
    public showButton = false;
    public closeIconLabel = ActionsButton.closeIconLabel;
    @Input() isEditMode: boolean = false;
    @Input() isEditted = false;
    @Input() structureData!: StructureInstance | null;
    @Output() resetIsEditted = new EventEmitter<boolean>();
    @Output() loadStructureId = new EventEmitter<number>();
    public structureStatus = StructureStatus;
    public actionsBtns = [
        {
            label: ActionsButton.flipTopLabel,
            action: ActionsButton.flipTopHint,
            revertaction: ActionsButton.revertBackHint,
        },
        {
            label: ActionsButton.filpLeftLabel,
            action: ActionsButton.flipLeftHint,
        },
        {
            label: ActionsButton.flipRightLabel,
            action: ActionsButton.flipRightHint,
        },
        {
            label: ActionsButton.flipFrontLabel,
            action: ActionsButton.flipForntHint,
        },
        {
            label: ActionsButton.flipBackLabel,
            action: ActionsButton.flipBackhint,
        },
    ];

    public saveBtns = [
        {
            iconlabel: SaveButton.saveStructureIconLabel,
            iconHint1: SaveButton.saveStructureIconHint,
        },
        {
            iconlabel: SaveButton.updateStructureIconLabel,
            iconHint1: SaveButton.updateStructureIconHint,
        },
        {
            iconlabel: SaveButton.publishIconLabel,
            iconHint1: SaveButton.publishIconHint,
        },
        {
            iconlabel: SaveButton.loadIconLabel,
            iconHint1: SaveButton.loadIconHint,
        },
    ];

    // Getter to filter buttons based on isEditMode
    get filteredSaveBtns() {
        return this.saveBtns.filter(
            (button) =>
                button.iconlabel === SaveButton.publishIconLabel ||
                (this.isEditMode &&
                    this.structureData!.status !== StructureStatus.Published &&
                    this.structureData!.status !== StructureStatus.Approved &&
                    button.iconlabel === SaveButton.updateStructureIconLabel) ||
                ((!this.isEditMode ||
                    this.structureData!.status === StructureStatus.Published ||
                    this.structureData!.status === StructureStatus.Approved) &&
                    button.iconlabel === SaveButton.saveStructureIconLabel) ||
                button.iconlabel === SaveButton.loadIconLabel,
        );
    }

    public meshes: Mesh[] = [];
    private outputPattern: CountOutputData[] = [];
    public autoCapturedImageUrl!: string;
    public structureId!: number;
    @Output() showSpinner = new EventEmitter<boolean>();
    @Output() openOnBoardingDialog = new EventEmitter<void>();
    @Input() autoCaptureCanvasFunction!: () => File;
    @Input() loadStructure!: (structureId: number) => void;

    public DesignerTourSteps = DesignerTourSteps;
    private subscriptions: Subscription[] = [];
    public isStepTourActive: boolean = false;

    constructor(
        private sceneManagerService: SceneManagerService,
        private dialog: MatDialog,
        private countCalculatorService: CountCalculatorService,
        private componentInteractionService: ComponentInteractionService,
        private dialogService: DialogService,
        private structureService: StructureService,
        private snackBar: MatSnackBar,
        private router: Router,
        private interactionService: InteractionService,
        private actionHelperService: ActionHelperService,
        private s3UploadService: S3UploadService,
        private route: ActivatedRoute,
        private customTourService: CustomTourService,
        private invalidService: InvalidShapeService,
        private translate: TranslateService,
        private copyPasteService: CopyPasteService,
        private meshVisualService: MeshVisualService,
        private auth: AuthService,
    ) {}

    ngOnInit(): void {
        const urlSegments = this.route.snapshot.url.map((segment) => segment.path);
        this.structureId = +urlSegments[1];

        const sceneManagerSub = this.sceneManagerService.meshes$.subscribe((meshes) => {
            this.meshes = meshes;
        });

        this.subscriptions.push(sceneManagerSub);

        const componentInteractionSub = this.componentInteractionService.getOutputPattern().subscribe((response) => {
            if (response.length === 1) {
                this.outputPattern.push(response[0]);
            } else {
                this.outputPattern = response;
            }
        });

        this.subscriptions.push(componentInteractionSub);
        /*** This will trigger When popUp has been close ****/
        this.customTourService.openActionsCall$.subscribe((res) => {
            this.showButton = res ? true : false;
            this.isOpen = res ? true : false;
            this.isStepTourActive = false;
        });

        /*** This will trigger when the step tour is Active ***/
        this.customTourService.stepShow$.subscribe((res) => {
            this.isStepTourActive = true;
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public toggleNav(): void {
        this.isOpen = !this.isOpen;
        this.showButton = !this.showButton;
    }

    public openDialogBox(): void {
        this.outputPattern = [];
        if (this.meshes.length) {
            this.showSpinner.emit(true);
            setTimeout(() => {
                try {
                    this.countCalculatorService.calculate(this.meshes);
                    this.dialog
                        .open(GetCountComponent, {
                            width: ActionButtonContainer.panelWidth,
                            panelClass: ActionButtonContainer.panelClassName,
                            data: this.outputPattern,
                            disableClose: true,
                        })
                        .afterClosed()
                        .subscribe((res) => {
                            if (!res) this.outputPattern = [];
                        });
                } finally {
                    this.showSpinner.emit(false);
                }
            }, 100);
        }
    }

    public actions(
        index: number,
        actionBtn: {
            label: string;
            action: string;
        },
    ): void {
        if (this.copyPasteService.copiedMeshes.length) {
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.disableRotationAction"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }
        const meshes = this.componentInteractionService.meshSelections.map((selection) => selection.mesh);
        this.actionHelperService.saveStateIfValid(meshes);

        // checking is that any mesh or meshes are overlapped due to duplicate
        if (this.interactionService.checkDuplicateOverlap(this.meshes)) {
            return;
        }
        meshes.forEach((mesh) => {
            switch (actionBtn.label) {
                case ActionsButton.flipTopLabel:
                    this.actionHelperService.flipTop(mesh);
                    break;
                case ActionsButton.filpLeftLabel:
                    this.actionHelperService.flipLeft(mesh);
                    break;
                case ActionsButton.flipRightLabel:
                    this.actionHelperService.flipRight(mesh);
                    break;
                case ActionsButton.flipFrontLabel:
                    this.actionHelperService.flipFront(mesh);
                    break;
                case ActionsButton.flipBackLabel:
                    this.actionHelperService.flipBack(mesh);
                    break;
                default:
                    break;
            }
        });
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public loginDialog(iconValue: SaveButton): void {
        const dialogData = {
            componentName: DialogInvokingComponents.LoginDialog,
        };
        const dialogRef = this.dialogService.openDialog(
            dialogData,
            "90%",
            { disableClose: true, maxHeight: "none" },
            "430px",
        );
        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                this.saveActions(iconValue);
            }
        });
    }

    public async saveActions(iconValue: SaveButton): Promise<void> {
        if (!this.auth.isUserLoggedIn) {
            this.loginDialog(iconValue);
            return;
        }
        this.interactionService.deselectAllMeshes();
        this.showSpinner.emit(true);

        try {
            switch (iconValue) {
                case SaveButton.saveStructureIconLabel:
                case SaveButton.updateStructureIconLabel:
                    await this.handleStructureSaveOrUpdate(iconValue);
                    break;

                case SaveButton.publishIconLabel:
                    await this.handlePublish();
                    break;

                case SaveButton.loadIconLabel:
                    await this.handleLoadStructure();
                    break;

                default:
                    this.showSpinner.emit(false);
                    break;
            }
        } catch (error) {
            this.showSpinner.emit(false);
        }
    }

    private async handleStructureSaveOrUpdate(iconValue: SaveButton, isForConfirmation?: boolean): Promise<void> {
        // checking is that any mesh or meshes are overlapped due to duplicate
        if (this.interactionService.checkDuplicateOverlap(this.meshes)) {
            this.showSpinner.emit(false);
            return;
        }
        this.outputPattern = [];
        if (!this.checkIfStructureCreated(iconValue)) {
            this.showSpinner.emit(false);
            return;
        }

        const hasInvalid = await this.invalidService.checkInvalid();
        if (hasInvalid) {
            this.showSpinner.emit(false);
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.shapeIsNotValid"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }

        // Stop spinner before showing dialog
        this.showSpinner.emit(false);

        const isSaveOperation = iconValue === SaveButton.saveStructureIconLabel;
        const shouldClearFields =
            isSaveOperation || (!this.isEditMode && this.structureData?.status === this.structureStatus.Saved);

        const dialogData = {
            componentName: DialogInvokingComponents.StructureDetails,
            title: isSaveOperation
                ? this.getTranslation("genericDialogBox.saveAndEditStructure.title1")
                : this.getTranslation("genericDialogBox.saveAndEditStructure.title2"),
            firstBtn: isSaveOperation
                ? this.getTranslation("genericDialogBox.button.saveBtn")
                : this.getTranslation("genericDialogBox.button.updateBtn"),
            secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            structureName: shouldClearFields ? "" : this.structureData?.structureName || "",
            structureDescription: shouldClearFields ? "" : this.structureData?.structureDescription || "",
        };
        this.dialogService
            .openDialog(dialogData, "35%", { disableClose: true })
            .afterClosed()
            .pipe(
                tap((response) => {
                    if (!response) this.outputPattern = [];
                }),
                filter((response) => !!response),
                switchMap(async (response) => {
                    this.showSpinner.emit(true);
                    return this.prepareAndSaveStructure(response, isSaveOperation);
                }),
                mergeMap((response) => response),
                finalize(() => this.showSpinner.emit(false)),
            )
            .subscribe({
                next: (response) => {
                    this.outputPattern = [];
                    if (isForConfirmation) {
                        this.router.navigateByUrl(`designer/${(response as any).structureId}`);
                        this.resetIsEditted.emit(false);
                        this.componentInteractionService.triggerReset();
                        this.loadStructures();
                    } else {
                        this.handleResponse(response);
                    }
                },
            });
    }

    private async prepareAndSaveStructure(dialogResponse: any, isSaveOperation: boolean): Promise<any> {
        // Get the file directly using the parent's function
        const file = this.autoCaptureCanvasFunction();
        const urls = await this.s3UploadService.multipartOperation(
            [file],
            S3UploadCategoryName.Structures,
            S3UploadFileType.Images,
        );
        this.autoCapturedImageUrl = urls[0];

        let fileUrl: string;
        let modelFaceInfoUrl: string;

        if (this.sceneManagerService.isSaveAsGLB) {
            // Export to GLB file and get the URL
            fileUrl = await this.sceneManagerService.exportToS3(
                S3UploadCategoryName.Structures,
                S3UploadFileType.GlbFiles,
                dialogResponse.structureName,
            );
        } else {
            // Upload JSON file and get the URL
            fileUrl = await this.s3UploadService.uploadStructureDataToS3(this.meshes, "models");
        }

        const dataFormat: ModelQuantityJson = {
            totalcount: this.meshes.length,
            faceinformation: this.outputPattern,
        };

        modelFaceInfoUrl = await this.s3UploadService.uploadStructureDataToS3(dataFormat, "model-Info");
        const body: StructureBaseModel = {
            structureName: dialogResponse.structureName,
            structureDescription: dialogResponse.structureDescription,
            structureData: fileUrl,
            isUpdated: true,
            status: StructureStatus.Saved,
            imageurl: this.autoCapturedImageUrl,
            modelQuantityJson: {
                modelInfoUrl: modelFaceInfoUrl,
                totalBlock: this.meshes.length,
            },
        };
        return isSaveOperation
            ? this.structureService.saveStructure(body)
            : this.structureService.updateStructure(this.structureData!.id, body);
    }

    private async handlePublish(): Promise<void> {
        //checking is that any mesh or meshes are overlapped due to duplicate
        const allmeshesInScene = this.sceneManagerService.getMeshes();
        if (this.interactionService.checkDuplicateOverlap(allmeshesInScene)) {
            this.showSpinner.emit(false);
            return;
        }
        this.outputPattern = [];
        if (!this.checkStructureStatus() || !this.checkIfStructureCreated(SaveButton.publishIconLabel)) {
            this.showSpinner.emit(false);
            return;
        }

        const hasInvalid = await this.invalidService.checkInvalid();
        if (hasInvalid) {
            this.showSpinner.emit(false);
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.shapeIsNotValid"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }

        // Stop spinner before showing dialog
        this.showSpinner.emit(false);

        if (this.isEditMode) {
            if (this.isEditted) {
                // Handle case where the structure has been edited
                this.handleEditedStructure();
            } else {
                // Handle case where no changes were made
                this.handleUnchangedStructure();
            }
        } else {
            // Handle the case when not in edit mode
            this.handleNonEditMode();
        }
    }

    private async handleLoadStructure(): Promise<void> {
        // checking is that any mesh or meshes are overlapped due to duplicate
        if (this.interactionService.checkDuplicateOverlap(this.meshes)) {
            this.showSpinner.emit(false);
            return;
        }

        if (!this.meshes.length || !this.isEditted) {
            await this.loadStructures();
            return;
        }

        await this.askToSaveStructure();
    }

    private async askToSaveStructure() {
        this.showSpinner.emit(false);
        const action = this.isEditMode ? "update" : "save";

        const dialogData = {
            componentName: DialogInvokingComponents.ConfirmationCurrentStructure,
            title: this.getTranslation("genericDialogBox.askToSaveTheStructure.title"),
            content: this.getTranslation("genericDialogBox.askToSaveTheStructure.content", { action }),
            firstBtn: this.getTranslation("genericDialogBox.button.replaceBtn"),
            secondBtn: this.isEditMode
                ? this.getTranslation("genericDialogBox.button.updateBtn")
                : this.getTranslation("genericDialogBox.button.saveBtn"),
        };

        this.dialogService
            .openDialog(dialogData, "42%", { disableClose: true })
            .afterClosed()
            .subscribe((response) => {
                if (response === ButtonData.Save) {
                    this.handleStructureSaveOrUpdate(
                        this.isEditMode ? SaveButton.updateStructureIconLabel : SaveButton.saveStructureIconLabel,
                        true,
                    );
                } else if (response === ButtonData.Replace) {
                    this.loadStructures();
                }
            });
    }

    private async loadStructures(): Promise<void> {
        // Stop spinner before showing dialog
        this.showSpinner.emit(false);

        // Open the dialog with structures list
        const data = {
            componentName: DialogInvokingComponents.LoadMyStructures,
            title: this.getTranslation("genericDialogBox.loadStructures.title"),
        };

        this.dialogService
            .openDialog(data, "50%", { disableClose: true })
            .afterClosed()
            .subscribe((selectedStructureId: number) => {
                if (!selectedStructureId) {
                    return;
                }
                // Handle the selected structure
                if (this.structureId === selectedStructureId) {
                    this.loadStructureId.emit(selectedStructureId);
                }
                this.router.navigate(["/designer", selectedStructureId], { replaceUrl: true });
            });
    }

    private handleNonEditMode(): void {
        const data = {
            componentName: DialogInvokingComponents.StructureDetails,
            title: this.getTranslation("genericDialogBox.SaveAndPublishStructure.title"),
            firstBtn: this.getTranslation("genericDialogBox.button.publishBtn"),
            secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            isPublish: true,
            structureName: this.structureData?.structureName || "",
            structureDescription: this.structureData?.structureDescription || "",
        };

        this.dialogService
            .openDialog(data, "70%", { disableClose: true })
            .afterClosed()
            .pipe(
                tap((ress) => {
                    if (!ress) this.outputPattern = [];
                }),
                // Filter out cases where the dialog was closed without a response
                filter((response) => !!response),
                switchMap(async (response) => {
                    this.showSpinner.emit(true);

                    // Get the file directly using the parent's function
                    const file = this.autoCaptureCanvasFunction();
                    const urls = await this.s3UploadService.multipartOperation(
                        [file],
                        S3UploadCategoryName.Structures,
                        S3UploadFileType.Images,
                    );
                    this.autoCapturedImageUrl = urls[0];

                    let fileUrl: string;
                    let modelFaceInfoUrl: string;
                    if (this.sceneManagerService.isSaveAsGLB) {
                        fileUrl = await this.sceneManagerService.exportToS3(
                            S3UploadCategoryName.Structures,
                            S3UploadFileType.Images,
                            response.structureName,
                        );
                    } else {
                        fileUrl = await this.s3UploadService.uploadStructureDataToS3(this.meshes, "models");
                    }

                    const dataFormat: ModelQuantityJson = {
                        totalcount: this.meshes.length,
                        faceinformation: this.outputPattern,
                    };
                    modelFaceInfoUrl = await this.s3UploadService.uploadStructureDataToS3(dataFormat, "model-Info");

                    const body: StructureBaseModel = {
                        structureName: response.structureName,
                        structureDescription: response.structureDescription,
                        structureData: fileUrl,
                        isUpdated: true,
                        status: StructureStatus.Published,
                        imageurl: this.autoCapturedImageUrl,
                        modelQuantityJson: {
                            modelInfoUrl: modelFaceInfoUrl,
                            totalBlock: this.meshes.length,
                        },
                    };
                    return this.structureService.saveAndPublishStructure(body);
                }),
                // Flatten the inner Observable
                mergeMap((response) => response),
                finalize(() => this.showSpinner.emit(false)),
            )
            .subscribe({
                next: (response) => {
                    this.outputPattern = [];
                    this.handleResponse(response);
                },
            });
    }

    private handleUnchangedStructure(): void {
        const data = {
            componentName: DialogInvokingComponents.MyStructuresList,
            title: this.getTranslation("genericDialogBox.publishStructure.title"),
            content: this.getTranslation("genericDialogBox.publishStructure.content"),
            firstBtn: this.getTranslation("genericDialogBox.button.publishBtn"),
            secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
        };

        this.dialogService
            .openDialog(data, "70%", { disableClose: true })
            .afterClosed()
            .pipe(
                tap((res) => {
                    if (!res) this.outputPattern = [];
                }),
                filter((response) => !!response), // Only proceed if result is truthy
                switchMap(() => {
                    this.showSpinner.emit(true);
                    return this.structureService.publishStructures([this.structureId]);
                }),
                finalize(() => this.showSpinner.emit(false)),
            )
            .subscribe({
                next: (response) => {
                    this.outputPattern = [];
                    this.handleResponse(response);
                },
            });
    }

    private handleEditedStructure(): void {
        const data = {
            componentName: DialogInvokingComponents.StructureDetails,
            title: this.getTranslation("genericDialogBox.updateAndPublishStructure.title"),
            content: this.getTranslation("genericDialogBox.updateAndPublishStructure.content"),
            firstBtn: this.getTranslation("genericDialogBox.button.publishBtn"),
            secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            structureName: this.structureData?.structureName || "",
            structureDescription: this.structureData?.structureDescription || "",
        };

        this.dialogService
            .openDialog(data, "70%", { disableClose: true })
            .afterClosed()
            .pipe(
                tap((res) => {
                    if (!res) this.outputPattern = [];
                }),
                filter((response) => !!response),
                switchMap(async (response) => {
                    this.showSpinner.emit(true);
                    // Get the file directly using the parent's function
                    const file = this.autoCaptureCanvasFunction();
                    const urls = await this.s3UploadService.multipartOperation(
                        [file],
                        S3UploadCategoryName.Structures,
                        S3UploadFileType.Images,
                    );
                    this.autoCapturedImageUrl = urls[0];

                    let fileUrl: string;
                    let modelFaceInfoUrl: string;
                    if (this.sceneManagerService.isSaveAsGLB) {
                        // Export to GLB file and get the URL
                        fileUrl = await this.sceneManagerService.exportToS3(
                            S3UploadCategoryName.Structures,
                            S3UploadFileType.GlbFiles,
                            response.structureName,
                        );
                    } else {
                        // Upload JSON file and get the URL
                        fileUrl = await this.s3UploadService.uploadStructureDataToS3(this.meshes, "models");
                    }

                    // dataType of Model Quantity json
                    const dataFormat: ModelQuantityJson = {
                        totalcount: this.meshes.length,
                        faceinformation: this.outputPattern,
                    };
                    // after uploading to s3 assign the url
                    modelFaceInfoUrl = await this.s3UploadService.uploadStructureDataToS3(dataFormat, "model-Info");
                    const body: StructureBaseModel = {
                        structureName: response.structureName,
                        structureDescription: response.structureDescription,
                        structureData: fileUrl,
                        isUpdated: true,
                        status: StructureStatus.Saved,
                        imageurl: this.autoCapturedImageUrl,
                        modelQuantityJson: {
                            modelInfoUrl: modelFaceInfoUrl,
                            totalBlock: this.meshes.length,
                        },
                    };
                    return this.structureService.updateAndPublishStructure(this.structureId, body);
                }),
                // Flatten the inner Observable
                mergeMap((response) => response),
                finalize(() => this.showSpinner.emit(false)),
            )
            .subscribe({
                next: (response) => {
                    this.outputPattern = [];
                    this.handleResponse(response);
                },
            });
    }

    private checkStructureStatus(): boolean {
        if (this.structureData?.status && this.structureData?.status !== StructureStatus.Saved) {
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.cannotPublishCurrentStatus"),
                SnackBarConfig.Actions.OKAY,
            );
            return false;
        }
        return true;
    }

    private checkIfStructureCreated(iconValue: SaveButton): boolean {
        const hasShape = this.meshes.length;

        const isSaveAction = [
            SaveButton.publishIconLabel,
            SaveButton.saveStructureIconLabel,
            SaveButton.updateStructureIconLabel,
        ].includes(iconValue);

        if (isSaveAction) {
            if (!hasShape) {
                const message =
                    iconValue === SaveButton.publishIconLabel
                        ? StructureResponseMessage.NoStructureCreatedBeforePublish
                        : StructureResponseMessage.NoStructureFound;

                this.openSnackBar(message, SnackBarConfig.Actions.OKAY);
                return false;
            }
            return true;
        }
        return false; // Handle any other icon values
    }

    public convertFileToDataURL(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    }

    public isDisabled(btn: { iconlabel: SaveButton; iconHint1: SaveButton }): boolean {
        const disabledConditions: Partial<Record<SaveButton, () => boolean>> = {
            [SaveButton.saveStructureIconHint]: () => !this.auth.isUserLoggedIn && !this.meshes.length,
            [SaveButton.updateStructureIconHint]: () => !this.isEditted,
            [SaveButton.publishIconHint]: () =>
                (!!this.structureData?.status && this.structureData?.status !== StructureStatus.Saved) ||
                (!this.auth.isUserLoggedIn && !this.meshes.length),
        };

        return disabledConditions[btn.iconHint1]?.() ?? false;
    }

    public handleResponse(data: any): void {
        this.openSnackBar(data.message, SnackBarConfig.Actions.OKAY);
        setTimeout(() => {
            this.router.navigateByUrl("/account/myStructures");
        }, 500);
    }

    public triggerOnBoardingDialog(): void {
        // Emit the event to the parent
        this.openOnBoardingDialog.emit();
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions to avoid memory leaks
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
